import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { ModificationRequestApi } from "@/config/apiClient";
import { createApiClient } from "@/config/createApiClient";
import { currentWorkspaceAtom } from "@/store";

const apiClient = createApiClient(ModificationRequestApi);

export const useModificationRequest = () => {
  const currentWorkspace = useAtomValue(currentWorkspaceAtom);

  const queryClient = useQueryClient();

  const useRefuseModificationRequest = () =>
    useMutation({
      mutationFn: (body: {
        modificationRequestId: string;
        orderId: string;
        orderItemId: string;
        comment?: string;
      }) =>
        apiClient.modificationRequestsControllerRefuseModificationRequest({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          modificationRequestId: body.modificationRequestId,
          modificationRequestAnswerCommentCommand: { comment: body.comment },
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          queryClient.invalidateQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
        ]),
    });

  const useAcceptModificationRequest = () =>
    useMutation({
      mutationFn: (body: {
        modificationRequestId: string;
        orderId: string;
        orderItemId: string;
        comment?: string;
      }) =>
        apiClient.modificationRequestsControllerAcceptModificationRequest({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          modificationRequestId: body.modificationRequestId,
          modificationRequestAnswerCommentCommand: { comment: body.comment },
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ["orders"] }),
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          queryClient.invalidateQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
        ]),
    });

  const useAddModificationRequest = (params: {
    orderId: string;
    orderItemId: string;
  }) =>
    useMutation({
      mutationFn: (
        body: Parameters<
          typeof apiClient.modificationRequestsControllerCreateModificationRequest
        >["0"]["modificationRequestCreateCommand"]
      ) =>
        apiClient.modificationRequestsControllerCreateModificationRequest({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          modificationRequestCreateCommand: body,
        }),
      onSuccess: () =>
        Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          queryClient.invalidateQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
        ]),
    });

  const useCancelModificationRequest = () =>
    useMutation({
      mutationFn: (params: {
        modificationRequestId: string;
        orderId: string;
        orderItemId: string;
      }) =>
        apiClient.modificationRequestsControllerCancelModificationRequest({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          modificationRequestId: params.modificationRequestId,
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          queryClient.invalidateQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
        ]),
    });

  return {
    useAcceptModificationRequest,
    useRefuseModificationRequest,
    useAddModificationRequest,
    useCancelModificationRequest,
  };
};
